import { css, type SerializedStyles } from "@emotion/react";
import styled from "@emotion/styled";

import { BaseLink } from "shared/components/common/base-link";
import { theme, transientOptions } from "shared/lib/styles";

import type { HeaderFeaturesLogoTextLinkSize } from "scmp-app/components/header/features/logo-text-link";
import { responsiveStyles } from "scmp-app/components/header/features/styles";
import type { ResponsiveStyledProps } from "scmp-app/components/header/features/types";
import IconLogoBilingualText from "scmp-app/components/header/icon-logo-with-bilingual-text.svg";
import IconLogoText from "scmp-app/components/header/icon-logo-with-text.svg";

export const Container = styled.div<ResponsiveStyledProps>`
  ${responsiveStyles}
`;

export const StyledLogoLink = styled(BaseLink)`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`;

export type StyledScmpLogoTextProps = {
  $size?: HeaderFeaturesLogoTextLinkSize;
};

const defaultSize = css`
  inline-size: auto;
  block-size: 18px;

  ${theme.breakpoints.up("desktop")} {
    block-size: 24px;
  }
`;

const smallSize = css`
  inline-size: auto;
  block-size: 18px;
`;

const largeSize = css`
  inline-size: auto;
  block-size: 18px;

  ${theme.breakpoints.up("tablet")} {
    block-size: 38px;
  }

  ${theme.breakpoints.up("desktop")} {
    block-size: 100%;
  }
`;

const sizeStyles: Record<HeaderFeaturesLogoTextLinkSize, SerializedStyles> = {
  default: defaultSize,
  large: largeSize,
  small: smallSize,
};

/**
 * The non-bilingual SVG has no internal spacing, so it needs external
 * `padding-block` at `large` size on desktop to match the header layout.
 */
export const StyledScmpLogoText = styled(IconLogoText, {
  ...transientOptions,
})<StyledScmpLogoTextProps>`
  ${({ $size = "default" }) => sizeStyles[$size]}

  ${({ $size }) =>
    $size === "large" &&
    css`
      ${theme.breakpoints.up("desktop")} {
        padding-block: 4px 16px;
      }
    `}
`;

/**
 * Homepage-only: rendered when bilingual=true (homepage desktop HK edition).
 * The bilingual SVG carries its own internal spacing, so no external
 * padding is needed — dimensions come from the shared `sizeStyles`.
 */
export const StyledScmpLogoBilingualText = styled(IconLogoBilingualText, {
  ...transientOptions,
})<StyledScmpLogoTextProps>`
  ${({ $size = "default" }) => sizeStyles[$size]}
`;
